import {
  ChangeDetectionStrategy,
  Component,
  Input,
  OnChanges,
  SimpleChanges,
} from '@angular/core';
import {
  BaseTeamCostSummaryItem,
  ScheduleAssignment,
  TCCompositionRole,
  TCService,
  TCTask,
  TCTransport,
  TeamCost,
  TeamCostView,
} from '@macs-frontend/features/company-management/team-cost/team-cost.types';
import { emptyTeamCost } from '@macs-frontend/features/company-management/team-cost/initial-objects/empty-team-cost';
import { toTime, toTimeWithoutDays } from '@macs-frontend/features/shared/utils/time';
import { ServiceTypeTitles } from '@macs-frontend/features/company-management/action-team/enums/services-titles.enum';
import { TaskTypeTitles } from '@macs-frontend/features/company-management/team-cost/enums/tasks-titles.enum';
import { ServiceTypes } from '@macs-frontend/features/shared';
import { TaskTypes } from '@macs-frontend/features/shared/enums/task-types.enum';

@Component({
  selector: 'md-teamcost',
  templateUrl: './team-cost.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TeamCostComponent implements OnChanges {
  @Input() teamCost: TeamCostView = emptyTeamCost;
  @Input() currencySign = '';
  @Input() showCosts = false;

  ngOnChanges(changes: SimpleChanges): void {
    //TODO:
  }

  getTasksTotalBillablePrice(tc: TeamCost): number {
    if (tc.tasksTotalBillablePrice) {
      return tc.tasksTotalBillablePrice;
    }

    if (!tc.tasks || !tc.tasks.length) {
      return 0;
    }

    const total = tc.tasks
      .map(task => task.billablePrice)
      .reduce((sum, item) => sum + item);

    return total || 0;
  }

  getSummaryItems(tc: TeamCost): BaseTeamCostSummaryItem[] {
    const baseTeamCostSummaryItems = [
      {
        title: 'Team Cost',
        cost: tc.compositionRolesTotalUnitPrice,
        total: tc.compositionRolesTotalBillablePrice,
      },
      {
        title: 'Services',
        cost: tc.servicesTotalUnitPrice,
        total: tc.servicesTotalBillablePrice,
      },
      {
        title: 'Tasks',
        cost: this.getTasksTotalCost(tc),
        total: this.getTasksTotal(tc),
      },
      {
        title: 'Materials',
        cost: this.getMaterialsTotalCost(tc),
        total: this.getMaterialsTotalBillable(tc),
      },
      {
        title: 'Transport',
        cost: tc.transportsTotalUnitPrice,
        total: tc.transportsTotalBillablePrice,
      },
      {
        title: 'Total cost',
        cost: tc.unitPrice,
        total: tc.billablePrice,
        isTotal: true,
      },
    ];

    return baseTeamCostSummaryItems as BaseTeamCostSummaryItem[];
  }

  getTasksTotalCost(tc: TeamCost): number {
    if (tc.tasksTotalUnitPrice) {
      return tc.tasksTotalUnitPrice;
    }

    if (!tc.tasks || !tc.tasks.length) {
      return 0;
    }

    const total = tc.tasks.map(task => task.unitPrice).reduce((sum, item) => sum + item);

    return total || 0;
  }

  getTasksTotal(tc: TeamCost): number {
    if (tc.tasksTotalBillablePrice) {
      return tc.tasksTotalBillablePrice;
    }

    if (!tc.tasks?.length) {
      return 0;
    }

    return tc.tasks.map(value => value.billablePrice).reduce((acc, obj) => acc + obj);
  }

  getMaterialsTotalCost(tc: TeamCost): number {
    return (tc.materials || []).reduce(
      (sum, r) => sum + (r.unitPrice || 0) * (r.qty || 0),
      0,
    );
  }

  getMaterialsTotalBillable(tc: TeamCost): number {
    return (tc.materials || []).reduce(
      (sum, r) => sum + (r.billablePrice || 0) * (r.qty || 0),
      0,
    );
  }

  protected readonly toTimeWithoutDays = toTimeWithoutDays;
  protected readonly toTime = toTime;
  protected readonly ServiceTypeTitles = ServiceTypeTitles;
  protected readonly TaskTypeTitles = TaskTypeTitles;

  getAssignmentsStartLocation(assignment: ScheduleAssignment): string {
    if (assignment.startLocationsAsString) {
      return assignment.startLocationsAsString;
    }

    return assignment.scheduleStartLocations[0]?.name || '';
  }

  getCompositionRoleRowTotal(compositionRole: TCCompositionRole): number {
    if (compositionRole.amount) {
      return compositionRole.amount;
    }
    return compositionRole.sellPrice * compositionRole.quantity;
  }

  getTransportsTotalBillablePrice(teamCost: TeamCostView): number {
    if (teamCost.transportsTotal) {
      return teamCost.transportsTotal;
    }
    return teamCost.transportsTotalBillablePrice || 0;
  }

  getTransportBillableRowTotal(transport: TCTransport): number {
    if (transport.billableTotalAmount) {
      return transport.billableTotalAmount;
    }

    return transport.totalBillablePrice * transport.qty;
  }

  filterTCServicesByType(services: TCService[], type: ServiceTypes) {
    return services.filter(s => s.type === type);
  }

  filterTCTasksByType(tasks: TCTask[], type: TaskTypes) {
    return tasks.filter(t => t.type === type);
  }

  readonly ServiceTypes = ServiceTypes;
  readonly TaskTypes = TaskTypes;
}
